import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { StatCard, ActionCard, Skeleton } from '@/components/ui';
import { useStats } from '@/hooks/use-stats';
import { AgentUsageChart, RecentActivityTable, TokenSavingsChart } from '@/components/dashboard';
import { useTenant } from '@/contexts/TenantContext';
import { useHasRole } from '@/lib/auth';
import { IDENTITY_PUBLIC_URL } from '@/lib/config';
import { StorageOverviewWidget, TenantStorageWidget } from '@/features/system';
import { useAnalyticsAccess, hasAnalyticsAccess } from '@/features/usage/hooks';

// =============================================================================
// Dashboard Page
// =============================================================================

export function DashboardPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { currentTenant, isLoading: tenantLoading } = useTenant();
  const { data: statsData, isLoading: statsLoading, error } = useStats();
  const { data: analyticsAccess } = useAnalyticsAccess();
  const isAdmin = useHasRole('admin');
  
  const isLoading = tenantLoading || statsLoading;

  const quickActions = [
    { 
      titleKey: 'admin.dashboard.quickActions.createAgent', 
      descKey: 'admin.dashboard.quickActions.createAgentDesc', 
      icon: 'bot' as const,
      path: '/agents/new',
      testId: 'quick-action-create-agent'
    },
    { 
      titleKey: 'admin.dashboard.quickActions.ingestDocs', 
      descKey: 'admin.dashboard.quickActions.ingestDocsDesc', 
      icon: 'upload' as const,
      path: '/knowledge/ingest',
      testId: 'quick-action-ingest-docs'
    },
    // Analytics entry point only visible with the analytics module
    // permission (REQ-ANL-002 — ISS-330). Fail-closed: hidden while
    // loading or on error.
    ...(hasAnalyticsAccess(analyticsAccess)
      ? [{
          titleKey: 'admin.dashboard.quickActions.viewMetrics', 
          descKey: 'admin.dashboard.quickActions.viewMetricsDesc', 
          icon: 'activity' as const,
          path: '/analytics',
          testId: 'quick-action-view-metrics'
        }]
      : []),
  ];

  // Build stats from API data with safe property access
  const stats = statsData ? [
    { 
      label: t('admin.dashboard.stats.activeAgents') || 'Active Agents', 
      value: statsData.agents?.active ?? 0, 
      change: `${statsData.agents?.total ?? 0} total`, 
      icon: 'bot' as const,
      testId: 'stat-agents'
    },
    { 
      label: t('admin.dashboard.stats.conversationsToday') || 'Conversations', 
      value: statsData.conversations?.total ?? 0, 
      change: `${statsData.conversations?.messages ?? 0} messages`, 
      icon: 'message' as const,
      testId: 'stat-conversations'
    },
    { 
      label: t('admin.dashboard.stats.documents') || 'Documents',
      value: statsData.collections?.documents ?? 0, 
      change: `${statsData.collections?.total ?? 0} collections`, 
      icon: 'file-text' as const,
      testId: 'stat-documents'
    },
    { 
      label: t('admin.dashboard.stats.groups') || 'Groups',
      value: statsData.groups?.total ?? 0, 
      change: `${statsData.users?.total ?? 0} users`, 
      icon: 'users' as const,
      testId: 'stat-groups'
    },
  ] : [];

  // No tenant selected - show selection prompt
  if (!currentTenant && !tenantLoading) {
    return (
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.dashboard.title')}</h1>
        </div>
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center space-y-4">
          <p className="text-amber-700 dark:text-amber-300 text-lg">
            {t('admin.dashboard.noTenantSelected') || 'Please select a tenant to view dashboard statistics.'}
          </p>
          {/* ISS-293: users without any workspace (social registration,
              invited users) can create it in the AR onboarding wizard. */}
          <a
            href={`${IDENTITY_PUBLIC_URL.replace(/\/$/, "")}/workspace/setup`}
            className="inline-block rounded-lg bg-[#2463eb] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e40af]"
          >
            Create your workspace
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.dashboard.title')}</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">{t('admin.dashboard.subtitle')}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {isLoading ? (
          <>
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-lg" />
            ))}
          </>
        ) : error ? (
          <div className="col-span-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-700 dark:text-red-300">
              Failed to load stats: {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        ) : (
          stats.map((stat, i) => (
            <StatCard
              key={i}
              label={stat.label}
              value={stat.value}
              change={stat.change}
              icon={stat.icon}
              testId={stat.testId}
            />
          ))
        )}
      </div>

      {/* Charts Row */}
      {!isLoading && !error && (
        <>
          <div className="grid gap-6 lg:grid-cols-2 mb-8">
            <AgentUsageChart 
              data={statsData?.agentUsage ?? []} 
              title={t('admin.dashboard.charts.agentUsage') || 'Usage by Agent'}
            />
            <TokenSavingsChart
              tenantId={currentTenant?.id}
              title={t('admin.dashboard.charts.tokenSavings') || 'Token Savings'}
            />
          </div>
          <div className="grid gap-6 mb-8">
            <RecentActivityTable 
              data={statsData?.recentActivity ?? []} 
              title={t('admin.dashboard.charts.recentActivity') || 'Recent Activity'}
            />
          </div>
        </>
      )}

      {/* Platform Storage Widgets (Admin Only) */}
      {isAdmin && (
        <div className="grid gap-6 lg:grid-cols-2 mb-8">
          <StorageOverviewWidget />
          <TenantStorageWidget />
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-testid="quick-actions">
        {quickActions.map((action, i) => (
          <ActionCard
            key={i}
            title={t(action.titleKey)}
            description={t(action.descKey)}
            icon={action.icon}
            onClick={() => navigate(action.path)}
            testId={action.testId}
          />
        ))}
      </div>
    </div>
  );
}
